/**
 * Common analytics utilities
 * Generic analytics handler for header/footer components
 *
 * Apps can register custom analytics trackers to pre-fill common CleverTap properties
 *
 * @example B2B App
 * import { trackUnifiedClpClick } from "@repo/bfl-b2b-libs/utils/unifiedClpAnalytics";
 * setAnalyticsConfig({ customTracker: trackUnifiedClpClick });
 *
 * @example Corporate App
 * import { trackCorporateEvent } from "@corporate-libs/utils/analytics";
 * setAnalyticsConfig({ customTracker: trackCorporateEvent });
 *
 * @example CPR App (no custom tracker)
 * // No setup needed - uses default dataLayer
 */

import { dataLayer } from "@repo/analytics/data-layer-utils";
import type { DataLayerProps, CombinedClevertapProps } from "@repo/analytics/data-layer-utils";

/**
 * Type for custom analytics tracker function
 *
 * Custom trackers should:
 * 1. Accept dynamic data from header/footer components
 * 2. Enrich with app-specific common properties (EP_BUSINESS, EP_JOURNEY, etc.)
 * 3. Call dataLayer() internally
 *
 * @example
 * // B2B unified tracker auto-fills: EP_BUSINESS="B2B", EP_JOURNEY="CLP", EP_PROPERTY_TYPE, etc.
 * export const trackUnifiedClpClick = async (loginStatus, props, cleverTapCustomProps) => {
 *   const enrichedProps = {
 *     ...cleverTapCustomProps,
 *     EP_BUSINESS: "B2B",
 *     EP_JOURNEY: "CLP",
 *     EP_CUSTOMER_ID: resolveCustomerId(),
 *     // ... 50+ other common properties
 *   };
 *   return dataLayer(loginStatus, props, enrichedProps);
 * };
 */
export type CustomAnalyticsTracker = (
  loginStatus: string,
  props: DataLayerProps,
  cleverTapCustomProps?: CombinedClevertapProps,
  preventCTEvent?: boolean
) => Promise<void>;

/**
 * Analytics configuration for the current app
 */
type AnalyticsConfig = {
  /**
   * Custom tracker function that enriches events with app-specific properties
   * If provided, will be used instead of default dataLayer
   */
  customTracker?: CustomAnalyticsTracker;
};

/**
 * Global analytics configuration
 * Set by each app at startup (in layout.tsx or _app.tsx)
 */
let analyticsConfig: AnalyticsConfig = {};

/**
 * Set analytics configuration for the app
 * Call this once at app startup (e.g., in layout.tsx useEffect)
 *
 * @param config - Analytics configuration object
 *
 * @example B2B App - Use unified tracker (auto-fills 50+ CT properties)
 * ```typescript
 * import { setAnalyticsConfig } from "@repo/header-footer/utils/AnalyticsUtils";
 * import { trackUnifiedClpClick } from "@repo/bfl-b2b-libs/utils/unifiedClpAnalytics";
 *
 * useEffect(() => {
 *   setAnalyticsConfig({ customTracker: trackUnifiedClpClick });
 * }, []);
 */
export const setAnalyticsConfig = (config: AnalyticsConfig) => {
  analyticsConfig = config;
};

/**
 * Common analytics handler for header/footer components
 *
 * If app registered a custom tracker (via setAnalyticsConfig), uses it.
 * Otherwise, falls back to standard dataLayer.
 *
 * Custom trackers receive:
 * - data: DataLayerProps with component, event, cta_text, etc.
 * - cleverTapCustomProps: Partial CT properties (EP_CLICK_TYPE, EP_SECTION, etc.)
 *
 * Custom trackers should enrich with common properties and call dataLayer internally.
 *
 * @param data - Structured data object for custom trackers (component, event, cta_text, etc.)
 * @param cleverTapCustomProps - Partial CleverTap properties for custom trackers (EP_CLICK_TYPE, EP_SECTION, etc.)
 * @param functionName - Name of calling function for logging purposes
 *
 */
export const trackAnalyticsEvent = async (
  data: DataLayerProps,
  cleverTapCustomProps?: CombinedClevertapProps,
  functionName: string = "Analytics"
): Promise<void> => {
  // If app registered custom tracker and provided required data, use it
  if (analyticsConfig.customTracker && data) {
    try {
      await analyticsConfig.customTracker("", data, cleverTapCustomProps);
      return;
    } catch (error) {
      console.warn(`[${functionName}] Custom tracker failed, falling back to dataLayer`, error);
    }
  }

  // Default: standard dataLayer (works for all apps without custom tracker)
  dataLayer("", data, cleverTapCustomProps);
};
